import React, { useState } from 'react';
import './FacilityFilter.css';

function FacilityFilter({ hierarchy, availableFacilityIds, selectedFacilityIds, onSelectionChange, onClose }) {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  // When data hasn't been loaded yet, availableFacilityIds is empty. In that case
  // treat every facility as available so the picker is usable pre-load, instead of
  // showing an empty tree.
  const hasAvailableFilter = availableFacilityIds && availableFacilityIds.size > 0;

  const getAllDescendantIds = (node) => {
    const ids = [];
    if (node.vitalEngine_Facility_ID__c) {
      const facilityId = node.vitalEngine_Facility_ID__c.toString();
      const facilityIdInt = Math.round(parseFloat(facilityId)).toString();

      if (!hasAvailableFilter || availableFacilityIds.has(facilityId) || availableFacilityIds.has(facilityIdInt)) {
        ids.push(facilityIdInt);
      }
    }
    if (node.children?.length > 0) {
      node.children.forEach(child => ids.push(...getAllDescendantIds(child)));
    }
    return ids;
  };

  const toggleNodeExpansion = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) newExpanded.delete(nodeId);
    else newExpanded.add(nodeId);
    setExpandedNodes(newExpanded);
  };

  const handleCheckboxChange = (node) => {
    const descendantIds = getAllDescendantIds(node);
    const newSelected = new Set(selectedFacilityIds);
    const anySelected = descendantIds.some(id => newSelected.has(id));

    if (anySelected) {
      descendantIds.forEach(id => newSelected.delete(id));
    } else {
      descendantIds.forEach(id => newSelected.add(id));
    }

    onSelectionChange(newSelected);
  };

  const isNodeChecked = (node) => {
    const descendantIds = getAllDescendantIds(node);
    if (descendantIds.length === 0) return false;
    return descendantIds.every(id => selectedFacilityIds.has(id));
  };

  const isNodeIndeterminate = (node) => {
    const descendantIds = getAllDescendantIds(node);
    if (descendantIds.length === 0) return false;
    const selectedCount = descendantIds.filter(id => selectedFacilityIds.has(id)).length;
    return selectedCount > 0 && selectedCount < descendantIds.length;
  };

  const hasAvailableData = (node) => {
    if (!hasAvailableFilter) return true;
    if (node.vitalEngine_Facility_ID__c) {
      const facilityId = node.vitalEngine_Facility_ID__c.toString();
      const facilityIdInt = Math.round(parseFloat(facilityId)).toString();
      if (availableFacilityIds.has(facilityId) || availableFacilityIds.has(facilityIdInt)) {
        return true;
      }
    }
    if (node.children?.length > 0) {
      return node.children.some(child => hasAvailableData(child));
    }
    return false;
  };

  const matchesSearch = (node) => {
    if (!searchTerm) return true;

    const search = searchTerm.toLowerCase();
    if (node.name?.toLowerCase().includes(search)) return true;
    if (node.vitalEngine_Facility_ID__c?.toString().includes(search)) return true;
    if (node.billingCity?.toLowerCase().includes(search)) return true;
    if (node.billingState?.toLowerCase().includes(search)) return true;
    if (node.children?.length > 0) return node.children.some(child => matchesSearch(child));
    return false;
  };

  const renderNode = (node, level = 0) => {
    if (!hasAvailableData(node) || !matchesSearch(node)) return null;

    const hasChildren = node.children && node.children.some(child => hasAvailableData(child));
    const isExpanded = expandedNodes.has(node.id);
    const isChecked = isNodeChecked(node);
    const isIndeterminate = isNodeIndeterminate(node);

    return (
      <div key={node.id} className="filter-node" style={{ marginLeft: `${level * 1.5}rem` }}>
        <div className="filter-node-content">
          {hasChildren ? (
            <button className="filter-expand-button" onClick={() => toggleNodeExpansion(node.id)}>
              {isExpanded ? '▼' : '▶'}
            </button>
          ) : <span className="filter-spacer" />}

          <label className="filter-checkbox-label">
            <input
              type="checkbox"
              checked={isChecked}
              ref={input => { if (input) input.indeterminate = isIndeterminate; }}
              onChange={() => handleCheckboxChange(node)}
              className="filter-checkbox"
            />
            <span className={`filter-node-name ${hasChildren ? 'has-children' : ''}`}>
              {node.name}
            </span>
            {node.vitalEngine_Facility_ID__c && (
              <span className="filter-node-id">
                ID: {Math.round(parseFloat(node.vitalEngine_Facility_ID__c))}
              </span>
            )}
          </label>
        </div>

        {hasChildren && isExpanded && (
          <div className="filter-children">
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const selectedCount = selectedFacilityIds.size;

  const countAllFacilities = (nodes) => {
    let count = 0;
    nodes.forEach(n => {
      if (n.vitalEngine_Facility_ID__c) count++;
      if (n.children?.length > 0) count += countAllFacilities(n.children);
    });
    return count;
  };

  const totalFacilities = countAllFacilities(hierarchy);
  const facilitiesWithData = hasAvailableFilter ? availableFacilityIds.size : totalFacilities;

  return (
    <div className="facility-filter-overlay" onClick={onClose}>
      <div className="facility-filter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="facility-filter-header">
          <h3>Filter by Facility</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="facility-filter-info">
          <p>
            {selectedCount} {selectedCount === 1 ? 'facility' : 'facilities'} selected
            <span className="facility-filter-counts">
              {' · '}Showing {facilitiesWithData} of {totalFacilities} facilities with data
            </span>
          </p>
        </div>

        <div className="facility-filter-search">
          <input
            type="text"
            placeholder="Search facilities by name, ID, city, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>
          )}
        </div>

        <div className="facility-filter-tree">
          {hierarchy.map(node => renderNode(node))}
        </div>

        <div className="facility-filter-footer">
          <button className="clear-button" onClick={() => onSelectionChange(new Set())}>
            Clear All
          </button>
          <button className="apply-button" onClick={onClose}>
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
}

export default FacilityFilter;
